import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Baby } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
// import { verifyCredentials, setCurrentUser } from '../utils/userStore';
import logo from '../assets/Logo.png';

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

const handleLogin = async (e) => {
  e.preventDefault();

  // Validate input first
  if (!formData.email || !formData.password) {
    toast.error("Vui lòng nhập đầy đủ email và mật khẩu!");
    return;
  }

  let res;
  let data = {};

  // Debug: Log the API URL being used
  console.log("API_BASE URL:", API_BASE);
  console.log("Calling login endpoint:", `${API_BASE}/login`);

  try {
    res = await fetch(`${API_BASE}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        email: formData.email,
        password: formData.password
      })
    });

    // Try to read the response body
    // Note: Some browser extensions (like rrweb recorder) may consume the body
    // before we can read it, so we need to handle that case
    try {
      data = await res.json();
    } catch (bodyError) {
      console.warn("Could not read response body:", bodyError.message);
      // Body was already consumed by a browser extension
      // Use status code to determine the error message
      data = { detail: null };
    }
  } catch (fetchError) {
    console.error("Fetch error:", fetchError);
    toast.error("Không thể kết nối đến server!");
    return;
  }

  console.log("Login response:", res.status, data);

  // Handle specific error cases
  // Use backend message if available, otherwise use status-based message
  if (res.status === 404) {
    toast.error(data.detail || "Email không tồn tại trong hệ thống. Vui lòng đăng ký tài khoản mới.");
    return;
  }

  if (res.status === 401) {
    toast.error(data.detail || "Mật khẩu không đúng. Vui lòng thử lại.");
    return;
  }

  if (res.status === 400) {
    toast.error(data.detail || "Yêu cầu không hợp lệ!");
    return;
  }

  if (!res.ok) {
    toast.error(data.detail || "Lỗi đăng nhập!");
    return;
  }

  // Success - Call login from AuthContext to update React state
  try {
    login({
      id: data.user_id,
      email: formData.email,
      currentWeek: data.pregnancy_week
    });

    localStorage.setItem("user_id", data.user_id);
    toast.success("Đăng nhập thành công!");
    navigate("/");
  } catch (navError) {
    console.error("Navigation error:", navError);
    toast.error("Lỗi điều hướng!");
  }
};

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-pink-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 group">
            <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
              <img
                src={logo}
                alt="Logo"
                className="w-10 h-10 object-contain"
              />
            </div>
            <span className="text-3xl font-bold bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent">
              AN NHI
            </span>
          </Link>
        </div>

        <Card className="border-pink-100 shadow-2xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-gray-800">
              Đăng nhập
            </CardTitle>
            <p className="text-center text-gray-600 mt-2">
              Chào mừng bạn trở lại!
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-gray-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="example@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 border-pink-200 focus:border-pink-400"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-700">Mật khẩu</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-1 border-pink-200 focus:border-pink-400"
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 text-pink-600" />
                  <span className="text-gray-600">Ghi nhớ tôi</span>
                </label>
                <a href="#" className="text-pink-600 hover:text-pink-700 font-medium">
                  Quên mật khẩu?
                </a>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white py-6 text-lg"
              >
                Đăng nhập
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Chưa có tài khoản?{' '}
                <Link to="/register" className="text-pink-600 hover:text-pink-700 font-semibold">
                  Đăng ký ngay
                </Link>
              </p>
            </div>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">Hoặc</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full mt-4 border-gray-300 hover:bg-gray-50"
                onClick={() => navigate('/')}
              >
                Tiếp tục không đăng nhập
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          Bằng việc đăng nhập, bạn đồng ý với{' '}
          <a href="#" className="text-pink-600 hover:underline">Diều khoản sử dụng</a> và{' '}
          <a href="#" className="text-pink-600 hover:underline">Chính sách bảo mật</a>
        </p>
      </div>
    </div>
  );
};

export default Login;